/* eslint-disable @typescript-eslint/no-empty-function */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { createPopper, Instance } from '@popperjs/core';
import {
  endOfDay,
  endOfYear,
  format,
  isAfter,
  isBefore,
  isValid,
  startOfDay,
} from 'date-fns';
import { take } from 'rxjs';
import { InputType } from '../../../../models/input-type.model';
import { DatepickerContainerComponent } from '../datepicker-container/datepicker-container.component';

const DATE_FORMAT = 'yyyy-MM-dd';

@Component({
  selector: 'lib-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: DatepickerComponent,
      multi: true,
    },
  ],
  standalone: false,
})
export class DatepickerComponent
  implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor
{
  datepickerRef: HTMLElement;
  datepickerContainerComponentRef: ComponentRef<DatepickerContainerComponent>;
  popperInstance: Instance;
  datepickerInput: HTMLInputElement;
  isMobile =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      navigator.userAgent,
    );
  isDisabled: boolean;
  value: string;
  notValid: boolean;
  earlyYear: boolean;
  lateYear: boolean;

  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() isInvalid: boolean;
  @Input() id: string;
  @Input() maxLength: number;
  @Input() isStartDate: boolean;
  @Input() ansokanCreatedDate: Date;
  @Input() maxDateIsFromFormSettings: boolean;
  @Input() label: string;
  @Input() ignoreEarlyYearValidation: boolean;

  @Output() datepickerBlur = new EventEmitter<void>();
  @Output() datepickerFocus = new EventEmitter<void>();

  @ViewChild('inputGroup') inputGroupRef: ElementRef;

  InputType = InputType;

  constructor(
    private cdr: ChangeDetectorRef,
    private viewContainerRef: ViewContainerRef,
    private zone: NgZone,
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (value) => {};
  onTouched = () => {};

  writeValue(obj: Date | string): void {
    if (obj) {
      this.addDateToInput(new Date(obj));
    }
  }

  registerOnChange(fn: () => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngAfterViewInit() {
    this.datepickerInput =
      this.inputGroupRef.nativeElement.querySelector('input');
    this.setMinMaxDate();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes['minDate'] && !changes['minDate'].firstChange) ||
      (changes['maxDate'] && !changes['maxDate'].firstChange)
    ) {
      this.setMinMaxDate();
    }
  }

  validate() {
    return (
      this.notValid && {
        invalidDate: true,
        isEarlyYear: this.earlyYear,
        isLateYear: this.lateYear,
      }
    );
  }

  ngOnDestroy(): void {
    this.hideDatepicker(true);
  }

  toggleDatepicker() {
    if (this.isDisabled) {
      return;
    }
    if (this.datepickerRef) {
      this.hideDatepicker(true);
    } else {
      this.showDatepicker();
    }
  }

  private showDatepicker() {
    if (this.isMobile) {
      this.datepickerInput.focus();
      return;
    }
    this.datepickerFocus.emit();
    //create a component reference
    this.datepickerContainerComponentRef =
      this.viewContainerRef.createComponent(DatepickerContainerComponent);

    this.datepickerContainerComponentRef.instance.selectedDate
      .pipe(take(1))
      .subscribe((date) => {
        this.onTouched();
        this.addDateToInput(date);
        this.hideDatepicker(true);
      });
    this.datepickerContainerComponentRef.instance.minDate = this.minDate;
    this.datepickerContainerComponentRef.instance.maxDate = this.maxDate;
    this.datepickerContainerComponentRef.instance.startThisYear = this.maxLength
      ? true
      : false;
    this.datepickerContainerComponentRef.instance.label = this.label;
    if (this.value) {
      this.datepickerContainerComponentRef.instance.initialDate = new Date(
        this.value,
      );
    }

    // get DOM element from component
    const domElem = (
      this.datepickerContainerComponentRef.hostView as EmbeddedViewRef<unknown>
    ).rootNodes[0] as HTMLElement;

    this.datepickerRef = document.createElement('div');
    this.datepickerRef.className = 'datepicker';
    this.datepickerRef.style.zIndex = '1090';
    this.datepickerRef.style.display = 'none';
    this.datepickerRef.append(domElem);
    document.body.append(this.datepickerRef);

    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        // timeout to allow angular to render full container before creating the popper
        this.datepickerRef.style.display = 'block';

        this.popperInstance = createPopper(
          this.datepickerInput,
          this.datepickerRef,
          {
            modifiers: [
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['top-end'],
                },
              },
              {
                name: 'offset',
                options: {
                  offset: [0, 4],
                },
              },
            ],
            placement: 'bottom-end',
            strategy: 'fixed',
          },
        );

        (document.querySelector('.datepicker-start') as HTMLElement)?.focus();
      });
    });
  }

  @HostListener('document:click', ['$event.target'])
  public checkOutsideClick(targetElement) {
    if (!this.datepickerRef || this.isMobile) {
      return;
    }

    const clickedInside =
      this.datepickerRef.contains(targetElement) ||
      this.inputGroupRef.nativeElement
        .querySelector('.open-calendar-btn')
        .contains(targetElement);
    if (!clickedInside) {
      this.hideDatepicker(false);
    }
  }

  @HostListener('document:keydown.esc', ['$event'])
  onEsc() {
    this.hideDatepicker(true);
  }

  public inputChange() {
    this.checkManualDate(
      this.inputGroupRef.nativeElement.querySelector('input').value,
    );
  }

  public checkManualDate(dateString: string) {
    this.onTouched();
    const dateStringArray = dateString.split('-');
    const validDay = this.checkDay(
      parseInt(dateStringArray[0]),
      parseInt(dateStringArray[1]),
      parseInt(dateStringArray[2]),
    );
    if (!validDay) dateString = '';
    this.addDateToInput(new Date(dateString));
  }

  private checkDay(y: number, m: number, d: number): boolean {
    switch (m) {
      case 2: {
        return d <= ((y % 4 === 0 && y % 100) || y % 400 === 0 ? 29 : 28);
      }
      case 4:
      case 6:
      case 9:
      case 11: {
        return d <= 30;
      }
      default: {
        return d <= 31;
      }
    }
  }

  private hideDatepicker(moveFocus: boolean) {
    if (!this.datepickerRef) {
      return;
    }
    this.datepickerBlur.emit();
    this.datepickerRef?.remove();
    this.datepickerRef = undefined;
    this.popperInstance?.destroy();
    this.popperInstance = undefined;
    this.datepickerContainerComponentRef.destroy();
    if (moveFocus) {
      this.inputGroupRef.nativeElement.querySelector('button').focus();
    }
  }

  private checkIfDateIsTooLate(date: Date) {
    if ((!this.maxLength && !this.maxDate) || !this.maxDate) return false;
    if (
      this.maxLength &&
      !this.isStartDate &&
      !this.maxDateIsFromFormSettings
    ) {
      return isAfter(date, endOfYear(this.maxDate));
    }
    return isAfter(date, endOfDay(this.maxDate));
  }

  private checkIfDateIsTooEarly(date: Date) {
    if (!this.maxLength && !this.minDate) return false;
    if (this.isStartDate && isBefore(this.minDate, this.ansokanCreatedDate))
      return isBefore(date, startOfDay(this.ansokanCreatedDate));
    return isBefore(date, startOfDay(this.minDate));
  }

  private addDateToInput(date: Date) {
    this.value = isValid(date) ? format(date, DATE_FORMAT) : '';
    this.earlyYear = this.ignoreEarlyYearValidation
      ? false
      : this.checkIfDateIsTooEarly(date);
    this.lateYear = this.checkIfDateIsTooLate(date);
    this.notValid = this.earlyYear || this.lateYear;
    if (this.onChange) {
      this.onChange(this.value ? new Date(this.value) : '');
      this.cdr.detectChanges();
    }
  }

  private setMinMaxDate() {
    if (this.isMobile) {
      if (this.minDate) {
        this.datepickerInput.setAttribute(
          'min',
          format(this.minDate, DATE_FORMAT),
        );
      }
      if (this.maxDate) {
        this.datepickerInput.setAttribute(
          'max',
          format(this.maxDate, DATE_FORMAT),
        );
      }
    }
  }
}
